import { useEffect, useMemo, useState } from "react";
import { Calculator, CalendarDays, CheckCircle, FileText, PenLine, Printer, Search, Trash2, UserRound } from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useFuncionarios, Funcionario } from "@/hooks/useFuncionarios";
import { calcularPagamentoFuncionario, PagamentoFuncionario, usePagamentosFuncionarios } from "@/hooks/usePagamentosFuncionarios";
import { useAgendamentos } from "@/hooks/useAgendamentos";
import { useOrdemServico } from "@/hooks/useOrdemServico";
import { useInformacoesClinica } from "@/hooks/useInformacoesClinica";
import { useClinicaDocumento } from "@/hooks/useClinicaDocumento";
import { endOfLocalDay, formatLocalDateBR, parseLocalDate, startOfLocalDay, toLocalISODate, todayLocalISO } from "@/lib/date-utils";
import { printReciboPagamentoFuncionario } from "@/lib/documentPrint";
import { calcularMeta, itensProducaoNoPeriodo, monthInputValue, periodoDoMes, periodoPessoa, producaoClinicaCompleta, producaoDentistaCompleta } from "@/lib/performance";
import { toast } from "sonner";

const formasPagamento = ["Pix", "Dinheiro", "Transferência", "Cartão", "Outro"];
const today = () => todayLocalISO();
const firstDayMonth = () => {
  const date = parseLocalDate(today());
  date.setDate(1);
  return toLocalISODate(date);
};
const money = (value?: number) => Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const recebeComissao = (funcionario?: Funcionario | null) =>
  !!funcionario && (funcionario.recebe_comissao || Number(funcionario.percentual_comissao || 0) > 0);
const recebeMeta = (funcionario?: Funcionario | null) =>
  !!funcionario && (funcionario.recebe_meta || Number(funcionario.meta_valor || 0) > 0 || Number(funcionario.meta_bonus || 0) > 0);

const producaoDoFuncionario = (funcionario: Funcionario, agendamentos: any[], ordensServico: any[], inicio: Date, fim: Date) =>
  funcionario.base_comissao === "producao_pessoal" && funcionario.dentista_id_comissao
    ? producaoDentistaCompleta(agendamentos, ordensServico, funcionario.dentista_id_comissao, inicio, fim)
    : producaoClinicaCompleta(agendamentos, ordensServico, inicio, fim);

const novoFormularioManual = () => ({
  competencia_inicio: firstDayMonth(),
  competencia_fim: today(),
  salario_base: 0,
  producao: 0,
  percentual_comissao: 0,
  bonus_meta: 0,
  descontos: 0,
  forma_pagamento: "Pix",
  data_pagamento: today(),
  status: "Pendente" as "Pendente" | "Pago" | "Cancelado",
  observacoes: "",
});

function statusVariant(status: string) {
  if (status === "Pago") return "default";
  if (status === "Cancelado") return "secondary";
  return "outline";
}

export function PagamentosFuncionarios() {
  const { funcionarios } = useFuncionarios();
  const { agendamentos, fetchAgendamentos } = useAgendamentos();
  const { ordensServico } = useOrdemServico();
  const { informacoes } = useInformacoesClinica();
  const { activeClinicaId, clinicaDocumento } = useClinicaDocumento(informacoes);
  const { pagamentos, loading, createPagamento, updatePagamento, deletePagamento } = usePagamentosFuncionarios();
  const storedClinicaName = typeof window !== "undefined" ? localStorage.getItem("odonto.activeClinicaName") || "" : "";
  const activeClinicaName = storedClinicaName || clinicaDocumento()?.nome_fantasia || clinicaDocumento()?.nome_clinica || clinicaDocumento()?.razao_social || "Unidade ativa";
  const [busca, setBusca] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState<Funcionario | null>(null);
  const [manualFuncionarioId, setManualFuncionarioId] = useState("");
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [modoPeriodo, setModoPeriodo] = useState<"mes" | "periodo">("mes");
  const [mesPeriodo, setMesPeriodo] = useState(monthInputValue());
  const [manualModoPeriodo, setManualModoPeriodo] = useState<"mes" | "periodo">("mes");
  const [manualMesPeriodo, setManualMesPeriodo] = useState(monthInputValue());
  const [formData, setFormData] = useState({
    competencia_inicio: firstDayMonth(),
    competencia_fim: today(),
    salario_base: 0,
    producao: 0,
    percentual_comissao: 0,
    bonus_meta: 0,
    descontos: 0,
    forma_pagamento: "Pix",
    data_pagamento: today(),
    status: "Pendente" as "Pendente" | "Pago" | "Cancelado",
    observacoes: "",
  });
  const [manualFormData, setManualFormData] = useState(novoFormularioManual);

  const funcionariosFiltrados = funcionarios.filter((funcionario) =>
    [funcionario.nome, funcionario.cargo, funcionario.email].filter(Boolean).join(" ").toLowerCase().includes(busca.toLowerCase())
  );

  const calculo = useMemo(() => calcularPagamentoFuncionario(formData), [formData]);
  const calculoManual = useMemo(() => calcularPagamentoFuncionario(manualFormData), [manualFormData]);
  const manualFuncionario = useMemo(
    () => funcionarios.find((funcionario) => funcionario.id === manualFuncionarioId) || null,
    [funcionarios, manualFuncionarioId]
  );
  const pagos = pagamentos.filter((item) => item.status === "Pago");
  const metaFechamento = useMemo(() => {
    if (!recebeMeta(funcionarioSelecionado)) return null;
    const inicio = startOfLocalDay(formData.competencia_inicio);
    const fim = endOfLocalDay(formData.competencia_fim);
    const producao = producaoDoFuncionario(funcionarioSelecionado, agendamentos, ordensServico, inicio, fim);
    return calcularMeta(funcionarioSelecionado, producao);
  }, [funcionarioSelecionado, formData.competencia_inicio, formData.competencia_fim, agendamentos, ordensServico]);

  const abrirFechamento = (funcionario: Funcionario) => {
    const periodo = periodoPessoa(funcionario);
    const usaPeriodoPerfil = !!(recebeMeta(funcionario) && funcionario.meta_inicio && funcionario.meta_fim);
    const competenciaInicio = usaPeriodoPerfil ? toLocalISODate(periodo.inicio) : firstDayMonth();
    const competenciaFim = usaPeriodoPerfil ? toLocalISODate(periodo.fim) : today();
    const producao = producaoDoFuncionario(funcionario, agendamentos, ordensServico, periodo.inicio, periodo.fim);
    const meta = calcularMeta(funcionario, producao);
    setModoPeriodo(usaPeriodoPerfil ? "periodo" : "mes");
    setMesPeriodo(monthInputValue(periodo.inicio));
    setFuncionarioSelecionado(funcionario);
    setFormData({
      competencia_inicio: competenciaInicio,
      competencia_fim: competenciaFim,
      salario_base: Number(funcionario.salario_base || 0),
      producao: recebeComissao(funcionario) ? producao : 0,
      percentual_comissao: recebeComissao(funcionario) ? Number(funcionario.percentual_comissao || 0) : 0,
      bonus_meta: recebeMeta(funcionario) ? meta.bonusLiberado : 0,
      descontos: 0,
      forma_pagamento: "Pix",
      data_pagamento: today(),
      status: "Pendente",
      observacoes: "",
    });
    setDialogOpen(true);
  };

  const abrirLancamentoManual = (funcionario?: Funcionario) => {
    const selecionado = funcionario || null;
    setManualFuncionarioId(selecionado?.id || "");
    setManualModoPeriodo("mes");
    setManualMesPeriodo(monthInputValue());
    setManualFormData({
      ...novoFormularioManual(),
      percentual_comissao: selecionado ? Number(selecionado.percentual_comissao || 0) : 0,
    });
    setManualDialogOpen(true);
  };

  const selecionarFuncionarioManual = (funcionarioId: string) => {
    const funcionario = funcionarios.find((item) => item.id === funcionarioId);
    setManualFuncionarioId(funcionarioId);
    setManualFormData((prev) => ({
      ...prev,
      percentual_comissao: Number(funcionario?.percentual_comissao || 0),
    }));
  };

  const aplicarMesManual = (mes: string) => {
    setManualMesPeriodo(mes);
    const periodo = periodoDoMes(mes);
    setManualFormData((prev) => ({
      ...prev,
      competencia_inicio: toLocalISODate(periodo.inicio),
      competencia_fim: toLocalISODate(periodo.fim),
    }));
  };

  const pagamentoDuplicado = (funcionarioId: string, inicio: string, fim: string) =>
    pagamentos.find((pagamento) =>
      pagamento.funcionario_id === funcionarioId &&
      pagamento.competencia_inicio === inicio &&
      pagamento.competencia_fim === fim &&
      pagamento.status !== "Cancelado"
    );

  const aplicarMesPeriodo = (mes: string) => {
    setMesPeriodo(mes);
    const periodo = periodoDoMes(mes);
    setFormData((prev) => ({
      ...prev,
      competencia_inicio: toLocalISODate(periodo.inicio),
      competencia_fim: toLocalISODate(periodo.fim),
    }));
  };

  const alterarModoPeriodo = (value: "mes" | "periodo") => {
    setModoPeriodo(value);
    if (value === "mes") aplicarMesPeriodo(mesPeriodo);
  };

  useEffect(() => {
    if (!dialogOpen || !formData.competencia_inicio || !formData.competencia_fim) return;
    fetchAgendamentos(startOfLocalDay(formData.competencia_inicio), endOfLocalDay(formData.competencia_fim));
  }, [dialogOpen, formData.competencia_inicio, formData.competencia_fim]);

  useEffect(() => {
    if (!dialogOpen || !funcionarioSelecionado) return;
    const inicio = startOfLocalDay(formData.competencia_inicio);
    const fim = endOfLocalDay(formData.competencia_fim);
    const producao = producaoDoFuncionario(funcionarioSelecionado, agendamentos, ordensServico, inicio, fim);
    const meta = calcularMeta(funcionarioSelecionado, producao);
    setFormData((prev) => ({
      ...prev,
      producao: recebeComissao(funcionarioSelecionado) ? producao : 0,
      percentual_comissao: recebeComissao(funcionarioSelecionado) ? Number(funcionarioSelecionado.percentual_comissao || 0) : 0,
      bonus_meta: recebeMeta(funcionarioSelecionado) ? meta.bonusLiberado : 0,
    }));
  }, [dialogOpen, funcionarioSelecionado?.id, formData.competencia_inicio, formData.competencia_fim, agendamentos, ordensServico]);

  const imprimirRecibo = (pagamento: PagamentoFuncionario) => {
    printReciboPagamentoFuncionario({
      funcionarioNome: pagamento.funcionario_nome,
      cargo: pagamento.cargo,
      competenciaInicio: pagamento.competencia_inicio,
      competenciaFim: pagamento.competencia_fim,
      dataPagamento: pagamento.data_pagamento,
      formaPagamento: pagamento.forma_pagamento,
      salarioBase: Number(pagamento.salario_base || 0),
      producao: Number(pagamento.producao || 0),
      percentualComissao: Number(pagamento.percentual_comissao || 0),
      valorComissao: Number(pagamento.valor_comissao || 0),
      bonusMeta: Number(pagamento.bonus_meta || 0),
      descontos: Number(pagamento.descontos || 0),
      valorTotal: Number(pagamento.valor_total || 0),
      observacoes: pagamento.observacoes,
      clinica: clinicaDocumento(pagamento),
    });
  };

  const salvarPagamento = async () => {
    if (!funcionarioSelecionado) return;
    if (formData.competencia_inicio > formData.competencia_fim) {
      toast.error("A data inicial não pode ser posterior à data final.");
      return;
    }
    const duplicado = pagamentoDuplicado(funcionarioSelecionado.id, formData.competencia_inicio, formData.competencia_fim);
    if (duplicado) {
      toast.error(`Já existe um fechamento ${duplicado.status.toLowerCase()} para esta pessoa e período.`);
      return;
    }
    const inicio = startOfLocalDay(formData.competencia_inicio);
    const fim = endOfLocalDay(formData.competencia_fim);
    const itens = itensProducaoNoPeriodo(agendamentos, ordensServico, inicio, fim)
      .filter((item) => funcionarioSelecionado.base_comissao === "producao_pessoal" && funcionarioSelecionado.dentista_id_comissao
        ? item.dentista_id === funcionarioSelecionado.dentista_id_comissao
        : true)
      .map(({ id, origem, data, descricao, valor }) => ({ id, origem, data, descricao, valor }));
    const pagamento = await createPagamento({
      funcionario_id: funcionarioSelecionado.id,
      funcionario_nome: funcionarioSelecionado.nome,
      cargo: funcionarioSelecionado.cargo,
      competencia_inicio: formData.competencia_inicio,
      competencia_fim: formData.competencia_fim,
      ...calculo,
      forma_pagamento: formData.forma_pagamento,
      data_pagamento: formData.status === "Pago" ? formData.data_pagamento : "",
      status: formData.status,
      origem_calculo: "automatico",
      base_comissao: funcionarioSelecionado.base_comissao || "unidade",
      producao_itens: itens,
      observacoes: formData.observacoes,
      clinica_id: activeClinicaId,
      clinica_dados: clinicaDocumento() || undefined,
    });
    if (pagamento) {
      setDialogOpen(false);
      if (pagamento.status === "Pago") imprimirRecibo(pagamento);
    }
  };

  const salvarLancamentoManual = async () => {
    if (!manualFuncionario) {
      toast.error("Selecione a pessoa do lançamento.");
      return;
    }
    if (!manualFormData.competencia_inicio || !manualFormData.competencia_fim || manualFormData.competencia_inicio > manualFormData.competencia_fim) {
      toast.error("Informe um período válido para o lançamento.");
      return;
    }
    if (manualFormData.percentual_comissao < 0 || manualFormData.percentual_comissao > 100) {
      toast.error("A comissão deve estar entre 0% e 100%.");
      return;
    }
    const duplicado = pagamentoDuplicado(manualFuncionario.id, manualFormData.competencia_inicio, manualFormData.competencia_fim);
    if (duplicado) {
      toast.error(`Já existe um lançamento ${duplicado.status.toLowerCase()} para ${manualFuncionario.nome} neste período.`);
      return;
    }

    const pagamento = await createPagamento({
      funcionario_id: manualFuncionario.id,
      funcionario_nome: manualFuncionario.nome,
      cargo: manualFuncionario.cargo,
      competencia_inicio: manualFormData.competencia_inicio,
      competencia_fim: manualFormData.competencia_fim,
      ...calculoManual,
      forma_pagamento: manualFormData.forma_pagamento,
      data_pagamento: manualFormData.status === "Pago" ? manualFormData.data_pagamento : "",
      status: manualFormData.status,
      origem_calculo: "manual",
      observacoes: manualFormData.observacoes,
      clinica_id: activeClinicaId,
      clinica_dados: clinicaDocumento() || undefined,
    });
    if (pagamento) {
      setManualDialogOpen(false);
      if (pagamento.status === "Pago") imprimirRecibo(pagamento);
    }
  };

  const marcarPago = async (pagamento: PagamentoFuncionario) => {
    const atualizado = await updatePagamento(pagamento.id, {
      status: "Pago",
      data_pagamento: today(),
      forma_pagamento: pagamento.forma_pagamento || "Pix",
    });
    if (atualizado) imprimirRecibo(atualizado);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold sm:text-3xl">Pagamento de Funcionários</h1>
            <p className="text-muted-foreground">Feche pagamentos por funcionário, com meta, comissão, descontos e recibo.</p>
          </div>
          <Button variant="outline" onClick={() => abrirLancamentoManual()}>
            <PenLine className="mr-2 h-4 w-4" />
            Lançar manualmente
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Funcionários</p><p className="text-2xl font-bold">{funcionarios.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Pagamentos no histórico</p><p className="text-2xl font-bold">{pagamentos.length}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Total pago</p><p className="text-2xl font-bold">{money(pagos.reduce((sum, item) => sum + Number(item.valor_total || 0), 0))}</p></CardContent></Card>
          <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Pendentes</p><p className="text-2xl font-bold">{pagamentos.filter((item) => item.status === "Pendente").length}</p></CardContent></Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Funcionários</CardTitle>
            <CardDescription>Selecione uma pessoa para gerar o fechamento individual.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Buscar por nome, cargo ou e-mail..." value={busca} onChange={(event) => setBusca(event.target.value)} />
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {funcionariosFiltrados.map((funcionario) => (
                <Card key={funcionario.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium">{funcionario.nome}</p>
                        <p className="text-sm text-muted-foreground">{funcionario.cargo}</p>
                      </div>
                      <Badge variant={funcionario.status === "Ativo" ? "default" : "secondary"}>{funcionario.status}</Badge>
                    </div>
                    <div className="mt-3 grid gap-1 text-sm text-muted-foreground">
                      <span>Salário base: {money(funcionario.salario_base)}</span>
                      <span>Comissão: {recebeComissao(funcionario) ? `${Number(funcionario.percentual_comissao || 0)}%` : "Não recebe"}</span>
                      <span>Meta: {recebeMeta(funcionario) ? `${money(funcionario.meta_valor)} | Bônus ${money(funcionario.meta_bonus)}` : "Não recebe"}</span>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-2">
                      <Button onClick={() => abrirFechamento(funcionario)}>
                        <Calculator className="mr-2 h-4 w-4" />
                        Automático
                      </Button>
                      <Button variant="outline" onClick={() => abrirLancamentoManual(funcionario)}>
                        <PenLine className="mr-2 h-4 w-4" />
                        Manual
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Histórico de Pagamentos</CardTitle>
            <CardDescription>Pagamentos gerados por funcionário, com reimpressão do recibo.</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-sm text-muted-foreground">Carregando pagamentos...</p>
            ) : pagamentos.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                <FileText className="mx-auto mb-3 h-10 w-10" />
                <p>Nenhum pagamento de funcionário gerado ainda.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pagamentos.map((pagamento) => (
                  <div key={pagamento.id} className="rounded-md border p-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <UserRound className="h-4 w-4 text-muted-foreground" />
                          <p className="font-medium">{pagamento.funcionario_nome}</p>
                          <Badge variant={statusVariant(pagamento.status)}>{pagamento.status}</Badge>
                          <Badge variant="outline">{pagamento.origem_calculo === "manual" ? "Manual" : "Automático"}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {pagamento.cargo || "Funcionário"} | {formatLocalDateBR(pagamento.competencia_inicio)} a {formatLocalDateBR(pagamento.competencia_fim)}
                        </p>
                        {pagamento.status === "Pago" && pagamento.data_pagamento && (
                          <p className="text-sm text-muted-foreground">Pago em {formatLocalDateBR(pagamento.data_pagamento)}</p>
                        )}
                        <p className="text-sm text-muted-foreground">
                          Base {money(pagamento.salario_base)} + comissão {money(pagamento.valor_comissao)} + meta {money(pagamento.bonus_meta)} - descontos {money(pagamento.descontos)}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        <p className="mr-2 text-lg font-bold">{money(pagamento.valor_total)}</p>
                        {pagamento.status !== "Pago" && (
                          <Button size="sm" onClick={() => marcarPago(pagamento)}>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Marcar pago
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => imprimirRecibo(pagamento)}>
                          <Printer className="mr-2 h-4 w-4" />
                          Recibo
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setDeleteId(pagamento.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-[680px]">
            <DialogHeader>
              <DialogTitle>Fechar pagamento {funcionarioSelecionado ? `- ${funcionarioSelecionado.nome}` : ""}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Como deseja calcular?</Label>
                  <Select value={modoPeriodo} onValueChange={alterarModoPeriodo}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mes">Mês inteiro</SelectItem>
                      <SelectItem value="periodo">Período personalizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {modoPeriodo === "mes" && (
                  <div className="space-y-2">
                    <Label>Mês de cálculo</Label>
                    <Input type="month" value={mesPeriodo} onChange={(event) => aplicarMesPeriodo(event.target.value)} />
                  </div>
                )}
              </div>

              {modoPeriodo === "periodo" && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Início da competência</Label>
                    <Input type="date" value={formData.competencia_inicio} onChange={(event) => setFormData({ ...formData, competencia_inicio: event.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Fim da competência</Label>
                    <Input type="date" value={formData.competencia_fim} onChange={(event) => setFormData({ ...formData, competencia_fim: event.target.value })} />
                  </div>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Salário base</Label>
                  <MoneyInput value={formData.salario_base} onValueChange={(salario_base) => setFormData({ ...formData, salario_base })} />
                </div>
                <div className="space-y-2">
                  <Label>Comissão (%)</Label>
                  <Input type="number" min="0" max="100" step="0.01" value={formData.percentual_comissao} readOnly className="bg-muted" />
                  <p className="text-xs text-muted-foreground">Percentual puxado do perfil.</p>
                </div>
                <div className="space-y-2">
                  <Label>Bônus de meta</Label>
                  <Input type="number" min="0" step="0.01" value={formData.bonus_meta} readOnly className="bg-muted" />
                  {metaFechamento && (
                    <p className="text-xs text-muted-foreground">
                      Meta {metaFechamento.percentual.toFixed(1)}% - {metaFechamento.bateu ? "bônus liberado" : `faltam ${money(metaFechamento.falta)}`}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Descontos</Label>
                  <MoneyInput value={formData.descontos} onValueChange={(descontos) => setFormData({ ...formData, descontos })} />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(value: any) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pendente">Pendente</SelectItem>
                      <SelectItem value="Pago">Pago</SelectItem>
                      <SelectItem value="Cancelado">Cancelado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Forma de pagamento</Label>
                  <Select value={formData.forma_pagamento} onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {formasPagamento.map((forma) => <SelectItem key={forma} value={forma}>{forma}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data do pagamento</Label>
                  <Input type="date" value={formData.data_pagamento} onChange={(event) => setFormData({ ...formData, data_pagamento: event.target.value })} />
                </div>
              </div>

              <div className="rounded-md border bg-muted/30 p-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-sm font-medium">Cálculo automático da comissão</p>
                    <p className="text-xs text-muted-foreground">
                      Produção realizada no período: {money(formData.producao)}. Percentual do perfil: {Number(formData.percentual_comissao || 0)}%.
                      Base: {funcionarioSelecionado?.base_comissao === "producao_pessoal" ? "produção do dentista vinculado" : "produção da unidade"}. Serviços repetidos entre agenda e ordem de serviço não são somados duas vezes.
                    </p>
                  </div>
                  <Badge variant={recebeComissao(funcionarioSelecionado) ? "default" : "secondary"}>
                    {recebeComissao(funcionarioSelecionado) ? "Automático" : "Sem comissão"}
                  </Badge>
                </div>
                {!recebeComissao(funcionarioSelecionado) && (
                  <p className="mt-2 text-xs text-muted-foreground">Para calcular comissão, informe o percentual no cadastro deste funcionário.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea rows={2} value={formData.observacoes} onChange={(event) => setFormData({ ...formData, observacoes: event.target.value })} placeholder="Ex.: adiantamento descontado, bônus liberado, pagamento parcial..." />
              </div>

              <div className="grid gap-2 rounded-md bg-muted/40 p-3 sm:grid-cols-5">
                <div><p className="text-xs text-muted-foreground">Salário</p><p className="font-semibold">{money(calculo.salario_base)}</p></div>
                <div><p className="text-xs text-muted-foreground">Comissão</p><p className="font-semibold">{money(calculo.valor_comissao)}</p></div>
                <div><p className="text-xs text-muted-foreground">Meta</p><p className="font-semibold">{money(calculo.bonus_meta)}</p></div>
                <div><p className="text-xs text-muted-foreground">Descontos</p><p className="font-semibold">{money(calculo.descontos)}</p></div>
                <div><p className="text-xs text-muted-foreground">Total</p><p className="font-bold">{money(calculo.valor_total)}</p></div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button onClick={salvarPagamento}>Salvar fechamento</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
          <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[620px]">
            <DialogHeader>
              <DialogTitle>Lançar pagamento manual</DialogTitle>
              <p className="text-sm text-muted-foreground">Unidade: {activeClinicaName}</p>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="manual-pessoa">Pessoa</Label>
                <Select value={manualFuncionarioId} onValueChange={selecionarFuncionarioManual}>
                  <SelectTrigger id="manual-pessoa"><SelectValue placeholder="Selecione o funcionário ou dentista" /></SelectTrigger>
                  <SelectContent>
                    {funcionarios.filter((item) => item.status !== "Inativo").map((funcionario) => (
                      <SelectItem key={funcionario.id} value={funcionario.id}>{funcionario.nome} - {funcionario.cargo || "Funcionário"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Período</Label>
                  <Select value={manualModoPeriodo} onValueChange={(value: "mes" | "periodo") => { setManualModoPeriodo(value); if (value === "mes") aplicarMesManual(manualMesPeriodo); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mes">Mês inteiro</SelectItem>
                      <SelectItem value="periodo">Personalizado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {manualModoPeriodo === "mes" && (
                  <div className="space-y-2">
                    <Label>Mês</Label>
                    <Input type="month" value={manualMesPeriodo} onChange={(event) => aplicarMesManual(event.target.value)} />
                  </div>
                )}
              </div>

              {manualModoPeriodo === "periodo" && (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2"><Label>Início</Label><Input type="date" value={manualFormData.competencia_inicio} onChange={(event) => setManualFormData({ ...manualFormData, competencia_inicio: event.target.value })} /></div>
                  <div className="space-y-2"><Label>Fim</Label><Input type="date" value={manualFormData.competencia_fim} onChange={(event) => setManualFormData({ ...manualFormData, competencia_fim: event.target.value })} /></div>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="manual-producao">Produção informada</Label>
                  <MoneyInput id="manual-producao" value={manualFormData.producao} onValueChange={(producao) => setManualFormData({ ...manualFormData, producao })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="manual-comissao">Comissão (%)</Label>
                  <Input id="manual-comissao" type="number" min="0" max="100" step="0.01" value={manualFormData.percentual_comissao} onChange={(event) => setManualFormData({ ...manualFormData, percentual_comissao: Number(event.target.value || 0) })} />
                  <p className="text-xs text-muted-foreground">Preenchida pelo perfil e liberada para ajuste manual.</p>
                </div>
              </div>

              <div className="rounded-md border bg-muted/30 p-3">
                <div className="grid grid-cols-3 gap-3">
                  <div><p className="text-xs text-muted-foreground">Produção</p><p className="font-semibold">{money(calculoManual.producao)}</p></div>
                  <div><p className="text-xs text-muted-foreground">Percentual</p><p className="font-semibold">{calculoManual.percentual_comissao}%</p></div>
                  <div><p className="text-xs text-muted-foreground">Comissão</p><p className="font-bold text-primary">{money(calculoManual.valor_comissao)}</p></div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-2"><Label>Salário/base adicional</Label><MoneyInput value={manualFormData.salario_base} onValueChange={(salario_base) => setManualFormData({ ...manualFormData, salario_base })} /></div>
                <div className="space-y-2"><Label>Bônus/meta</Label><MoneyInput value={manualFormData.bonus_meta} onValueChange={(bonus_meta) => setManualFormData({ ...manualFormData, bonus_meta })} /></div>
                <div className="space-y-2"><Label>Descontos</Label><MoneyInput value={manualFormData.descontos} onValueChange={(descontos) => setManualFormData({ ...manualFormData, descontos })} /></div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={manualFormData.status} onValueChange={(value: "Pendente" | "Pago" | "Cancelado") => setManualFormData({ ...manualFormData, status: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="Pendente">Pendente</SelectItem><SelectItem value="Pago">Pago</SelectItem><SelectItem value="Cancelado">Cancelado</SelectItem></SelectContent>
                  </Select>
                </div>
                {manualFormData.status === "Pago" && (
                  <div className="space-y-2"><Label>Data do pagamento</Label><Input type="date" value={manualFormData.data_pagamento} onChange={(event) => setManualFormData({ ...manualFormData, data_pagamento: event.target.value })} /></div>
                )}
              </div>

              {manualFormData.status === "Pago" && (
                <div className="space-y-2">
                  <Label>Forma de pagamento</Label>
                  <Select value={manualFormData.forma_pagamento} onValueChange={(value) => setManualFormData({ ...manualFormData, forma_pagamento: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{formasPagamento.map((forma) => <SelectItem key={forma} value={forma}>{forma}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea rows={2} value={manualFormData.observacoes} onChange={(event) => setManualFormData({ ...manualFormData, observacoes: event.target.value })} placeholder="Motivo do ajuste ou detalhes do pagamento..." />
              </div>

              <div className="flex items-center justify-between rounded-md bg-muted/40 p-3">
                <span className="text-sm text-muted-foreground">Total do lançamento</span>
                <strong className="text-xl">{money(calculoManual.valor_total)}</strong>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setManualDialogOpen(false)}>Cancelar</Button>
                <Button onClick={salvarLancamentoManual}>{manualFormData.status === "Pago" ? "Salvar como pago" : "Registrar lançamento"}</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Excluir pagamento</AlertDialogTitle>
              <AlertDialogDescription>Tem certeza que deseja excluir este pagamento? Esta ação não pode ser desfeita.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={async () => { if (deleteId) await deletePagamento(deleteId); setDeleteId(null); }}>Excluir</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
